import {
  AtSign,
  ChevronLeft,
  ChevronRight,
  Clock,
  Computer,
  Globe,
  Laptop,
  Layers,
  PcCase,
  Router,
  ScanBarcode,
  Wifi,
  Zap,
} from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";
import { Item, ItemContent } from "components/ui/item";
import { CoordinatesToGoogle } from "components/CoordinatesToGoogle";
import { DetailItem } from "components/DetailItem";
import { formatDate } from "components/helpers";

const joinList = values => (values?.length ? values.join(", ") : null);

/**
 * The pager over a stack of events sharing one coordinate.
 *
 * Newer is on the left and older on the right, so stepping right walks back in
 * time — the stack is ordered newest first.
 */
function EventPager({ index, count, onShowNewer, onShowOlder }) {
  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
      <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:font-medium tw:text-(--color-text-secondary)">
        <Layers className="tw:size-3.5 tw:shrink-0" />
        <span>{count} events at this location</span>
      </div>
      <div className="tw:flex tw:items-center tw:gap-1">
        <Button
          variant="outline"
          size="icon-xs"
          aria-label="Newer event"
          disabled={index === 0}
          onClick={onShowNewer}
        >
          <ChevronLeft />
        </Button>
        <span className="tw:text-xs tw:tabular-nums tw:text-(--color-text-muted)">
          {index + 1}/{count}
        </span>
        <Button
          variant="outline"
          size="icon-xs"
          aria-label="Older event"
          disabled={index === count - 1}
          onClick={onShowOlder}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

/**
 * The events at one coordinate, one at a time, shown when its overlay marker is
 * clicked.
 *
 * The field set and labels mirror AppMap's location summary so an analyst reads
 * the same vocabulary on both maps.
 *
 * `events` are ordered newest first, and the pager opens on the newest.
 */
export function EventInfoCard({ events, position }) {
  const [index, setIndex] = useState(0);

  const count = events.length;
  // A refetch can hand over a shorter stack while the summary is open, and an
  // offset past its end would render nothing at all. The pager steps from the
  // clamped value for the same reason: stepping from the raw index would spend
  // a press per unit of drift before the card moved.
  const current = Math.min(index, count - 1);
  const event = events[current];

  const location = [event.public_ip_city, event.public_ip_state]
    .filter(Boolean)
    .join(", ");

  const showNewer = () => setIndex(Math.max(current - 1, 0));
  const showOlder = () => setIndex(Math.min(current + 1, count - 1));

  return (
    <div className="tw:flex tw:max-h-80 tw:min-w-64 tw:flex-col tw:gap-2 tw:p-2">
      {count > 1 && (
        <Item variant="muted">
          <ItemContent>
            <EventPager
              index={current}
              count={count}
              onShowNewer={showNewer}
              onShowOlder={showOlder}
            />
          </ItemContent>
        </Item>
      )}

      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-1.5 tw:overflow-y-auto">
        <DetailItem icon={Zap} label="Event type" content={event?.event_type} />
        <DetailItem
          icon={ScanBarcode}
          label="Event product"
          content={event?.product}
        />
        <DetailItem
          icon={Clock}
          label="Server time"
          content={
            event.server_timestamp ? formatDate(event.server_timestamp) : null
          }
        />
        <DetailItem icon={Laptop} label="Machine" content={event.machine_id} />
        <DetailItem icon={PcCase} label="Hostname" content={event.hostname} />
        <DetailItem
          icon={Wifi}
          label="WiFi AP"
          content={joinList(event.active_wifi_access_point)}
        />
        <DetailItem
          icon={Globe}
          label="Public IP"
          content={
            location
              ? `${event.public_ip_address || "—"} (${location})`
              : event.public_ip_address
          }
        />
        <DetailItem
          icon={AtSign}
          label="Email domains"
          content={joinList(event.email_domains)}
        />
        <DetailItem
          icon={Computer}
          label="Computer domain"
          content={event.computer_domain}
        />
        <DetailItem
          icon={Router}
          label="Active MAC"
          content={event.active_mac}
        />
      </div>

      <Separator />

      <CoordinatesToGoogle position={position} />
    </div>
  );
}
